// ─────────────────────────────────────────────────────────────────────────
// Agent Tutor design tokens
//
// Re-based on adminColors' cool zinc neutrals — the tutor is part of the
// same product as the admin dashboard and reads that way: same ink, same
// surfaces, same radius and shadow language, one typeface. Nothing here
// diverges from the rest of the site on purpose; the only things this file
// adds beyond adminColors are the semantic verdict/hint palette a solver
// needs that a dashboard never did, all tuned to the same cool, restrained
// register instead of a separate warm one.
//
// Every Agent Tutor component reads from `tutorTokens`, not adminColors
// directly, so this file is the one place a future pass touches to restyle
// the rest of the tab.
// ─────────────────────────────────────────────────────────────────────────

import { adminColors } from './adminTheme';

export const tutorTokens = {
  // Brand — inherited so the tutor never drifts from the product's identity.
  accent: adminColors.accent, // #4f46e5
  accentDark: adminColors.accentDark,
  accentLight: adminColors.accentLight,
  accentBorder: adminColors.accentBorder,

  // Neutrals — the same cool zinc ramp as the admin dashboard, not a
  // separate warm one. `ink`/`inkSoft`/`surface`/`surfaceRaised` are
  // adminColors' own values, not re-derived, so the tutor can never drift
  // from the rest of the site by an edit here.
  ink: adminColors.ink, // #18181b
  inkSoft: adminColors.inkSoft, // #52525b
  // #71717a (zinc-500, the obvious next step down from inkSoft) measures
  // 4.51:1 on `surface` — technically over the 4.5:1 AA floor but with a
  // margin thin enough to flip below it from routine rounding/rendering
  // variance. #6b6b74 verifies at 4.93:1 on surface (#f7f7f8) and 5.28:1 on
  // surfaceRaised (#ffffff), same zinc undertone, comfortable margin on
  // both. This token is used at 10-13px on eyebrows/labels/indices, none of
  // which qualify as "large text" under WCAG's 18pt/14pt-bold exemption.
  inkFaint: '#6b6b74',
  surface: adminColors.surface, // #f7f7f8 — page background
  surfaceRaised: adminColors.surfaceRaised, // #ffffff — cards / rail / problem bar
  border: '#e4e4e7', // zinc-200
  borderStrong: '#d4d4d8', // zinc-300
  // Neutral hairline for dense in-card lists (step rows, glance rows,
  // activity toggles) — quieter than `border`, which is for a shape's own
  // edge. Same rgba(24,24,27,…) neutral language as the shadow tokens
  // below, just at list-divider strength.
  divider: 'rgba(24,24,27,0.08)',

  // Verdicts — never color-only in the UI, but the color still needs to be
  // unambiguous at a glance and distinguishable for common CVD types.
  // These render as text in three places: directly on `surface`/
  // `surfaceRaised` (the step feedback line), and on their own tinted chip
  // background (VerdictTag) — verified in all four spots, worst case first:
  // correct 5.18:1 (on correctBg) / 5.50:1 (surface) / 5.88:1
  // (surfaceRaised); partial 5.53:1 (on partialBg) / 5.54:1 / 5.93:1;
  // incorrect 4.92:1 (on incorrectBg) / 5.41:1 / 5.80:1. All clear 4.5:1
  // with real margin — the previous, lighter greens/ambers cleared 4.5:1 on
  // surface/surfaceRaised but dropped to ~4.4:1 on their own chip
  // background, which is the binding case for VerdictTag's 11-12px bold
  // label. Backgrounds are cool tints (Google's own semantic ramp), not the
  // previous palette's warm cream ones.
  correct: '#14742f',
  correctBg: '#e6f4ea',
  correctBorder: '#bfe2c2',
  partial: '#a34a08',
  partialBg: '#fef7e0',
  partialBorder: '#f0d29c',
  incorrect: '#c5221f',
  incorrectBg: '#fce8e6',
  incorrectBorder: '#f3c4c0',

  // Hint ladder — a distinct, quieter hue from the verdict colors so
  // "help was used" never reads as "you were wrong," and distinct from the
  // indigo accent so it never reads as the primary action. Verified 6.64:1
  // on surface / 7.10:1 on surfaceRaised as text.
  hint: '#6d28d9',
  hintBg: '#f5f3ff',
  hintBorder: '#ddd6fe',

  // Type — one family, the site's own. Hierarchy comes from size, weight,
  // color, and spacing, not a second typeface. Top-level page titles use
  // MUI's `variant="h4"` (34px/600, inherited unstyled from adminTheme) —
  // not a hand-set responsive fontSize — so wayfinding headings match every
  // other admin page exactly. In-page card/section/step headings (e.g.
  // "Hinting Strategy", panel titles) stay at their own smaller sizes.
  //
  // Button-label casing rule, documented here since it isn't visible from
  // any one file: Title Case for page-level and admin CTAs ("Build Your
  // Own Agent", "Publish to N classes"), matching the rest of the admin
  // product. Sentence case for in-flow controls inside the workbench itself
  // ("New problem", "Check step", "Solve") — those read as a running
  // conversation with the student, not a dashboard action bar.
  fontUI: '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',

  // Type scale — one ramp, applied everywhere on Agent Tutor (student
  // workbench + rail, and the admin library/config/wizard) instead of the
  // arbitrary one-off fontSize values (0.68rem, 0.74rem, 0.76rem, 0.82rem,
  // 0.83rem, 0.86rem, 0.88rem, 0.92rem, 0.98rem, 1.05rem… all in the same
  // few components) that made chat bubbles, MCQ options, misconception rows
  // and the stats row each read at a subtly different size.
  //
  // Fluid, not fixed — each role is `clamp(floor, intercept + slope·vw,
  // ceiling)`. `floor` is the exact rem value this scale shipped with
  // originally: nothing renders smaller than that at any viewport, laptop
  // widths included (the interpolation is anchored so the floor is reached
  // *at* 1400px viewport width, not above it, and `clamp()`'s own lower
  // bound holds below that regardless of the linear term). `ceiling` is
  // reached at 2000px and never exceeded past it. Between those two
  // viewports every role grows linearly, with more room given to the
  // larger/heading roles (eyebrow +9%, label +12%, body +14%, bodyLg
  // +16%, subtitle +19%, title +23%, display +29% at the ceiling) than the
  // small chrome roles — a wide display should look more confidently
  // scaled, not just uniformly bigger. `line-height` and `letterSpacing`
  // are left as unitless/`em` values on purpose: both already scale
  // proportionally with a fluid `font-size` for free.
  //
  // Each role is a ready-to-spread sx fragment: `sx={{ ...tutorTokens.type
  // .body, color: ... }}`. Roles carry size/line-height/weight only —
  // never color, so every call site still themes its own state (ink vs
  // inkSoft vs a verdict color) on top.
  type: {
    // 11px → 12px. Eyebrows: GIVEN, stat labels, index numbers, topic/
    // example kickers, misconception meta. Always paired with
    // letterSpacing here since every current use of this size is also a
    // tracked all-caps label.
    eyebrow: { fontSize: 'clamp(0.6875rem, 0.5417rem + 0.167vw, 0.75rem)', lineHeight: 1.4, fontWeight: 700, letterSpacing: '0.05em' },
    // 12.5px → 14px. Verdict tags, hint-ladder pills, status chips,
    // timestamps, "N hints used" asides — small UI furniture, not
    // reading prose.
    label: { fontSize: 'clamp(0.78rem, 0.5583rem + 0.253vw, 0.875rem)', lineHeight: 1.4, fontWeight: 600 },
    // 14px → 16px. The workhorse: chat bubbles, MCQ question/options,
    // flashcard copy, step feedback lines, misconception mistake/response
    // text, notices, panel description copy. Default weight 400; call
    // sites bump to 600–700 for their own emphasis rather than a second
    // body role.
    body: { fontSize: 'clamp(0.875rem, 0.5833rem + 0.333vw, 1rem)', lineHeight: 1.55, fontWeight: 400 },
    // 16px → 18.5px. The one thing a student actively types or reads
    // closest: working-pad step rows, the active input, the entry hero's
    // input.
    bodyLg: { fontSize: 'clamp(1rem, 0.6354rem + 0.417vw, 1.15625rem)', lineHeight: 1.5, fontWeight: 500 },
    // 18px → 21.5px. Every card/section heading: SectionTitle, OptionCard's
    // main label, "Your Working", "Misconceptions", the rail's agent name.
    subtitle: { fontSize: 'clamp(1.125rem, 0.6146rem + 0.583vw, 1.34375rem)', lineHeight: 1.35, fontWeight: 700 },
    // 22px → 27px. The active problem statement's fallback size and the
    // compact (embedded) entry hero heading.
    title: { fontSize: 'clamp(1.375rem, 0.6458rem + 0.833vw, 1.6875rem)', lineHeight: 1.3, fontWeight: 600 },
    // 28px → 36px. The full-page entry hero "What are we solving today?"
    // only.
    display: { fontSize: 'clamp(1.75rem, 0.5833rem + 1.333vw, 2.25rem)', lineHeight: 1.25, fontWeight: 600 },
  },

  // Layout — one page-width strategy and one card/section rhythm for every
  // Agent Tutor surface (library, wizard, config, session), so a student or
  // teacher moving between them never lands on a page that solved gutters,
  // padding, or content width independently. Before this, the library and
  // config pages went edge-to-edge with only `p:{xs:2,sm:3}`, the wizard
  // centered a ~900px column inside that same full-bleed page, and the
  // session workspace capped at 1240 — three different answers to "how
  // wide is a page" in one feature. That got unified once already on a
  // fixed 1240 cap; on a ~2000px display that fixed cap itself became the
  // problem (~380px of dead gutter on each side).
  layout: {
    // The one content measure: 96% of the *actual available container*,
    // capped at 1900px for very wide displays. `%`, not `vw` — this was
    // `min(96vw, 1900px)` originally, which measures the raw browser
    // viewport. That's correct for the tutor session (a top-level route
    // with no sidebar, so its container genuinely is the viewport) but
    // silently wrong for every admin page (library, wizard, config),
    // which all render inside AdminDashboardV2's shell — a 280px sidebar
    // narrows the real available width well below the viewport (e.g.
    // 1322px of actual space at a 1650px viewport, ~262px less than 96vw
    // computes there). `vw` can't see that; it only ever measures the
    // window. `%` resolves against this element's own containing block —
    // whatever DOM parent it actually has — so it's automatically correct
    // in both places: full width when the parent is the viewport itself,
    // sidebar-adjusted width when the parent is the admin shell's `main`.
    // Same class of bug as `twoColAt760` a few rounds back (viewport
    // media query instead of container width) — this is the calc()
    // version of it.
    maxWidth: 'min(96%, 1900px)',
    // Same responsive gutter on every page's outer padding.
    gutter: { xs: 2, sm: 3 },
    // Card interior padding for a "real content" card (a config panel, a
    // wizard step panel) — matches the admin dashboard's own card padding
    // at laptop widths (24px) and grows modestly on wide displays so a
    // card's own whitespace scales with everything inside it.
    cardPad: 'clamp(24px, 1.6vw, 32px)',
    // Vertical rhythm between major sections/cards on a page — same floor/
    // growth shape as `cardPad`.
    sectionGap: 'clamp(24px, 1.6vw, 32px)',
  },

  // Radius scale — matches the site's (adminTheme: 8 / 10 / 14 / 16).
  radiusSm: 8,
  radiusMd: 10,
  radiusLg: 14,
  radiusXl: 16,

  // Shadows — the site's own neutral, very subtle language
  // (rgba(24,24,27,…), same as adminTheme's MuiCard/MuiPaper overrides).
  // Prefer a hairline border over a shadow wherever both would work.
  shadowCard: '0 1px 2px rgba(24,24,27,0.04), 0 1px 0 rgba(24,24,27,0.06)',
  shadowFloating: '0 12px 32px -12px rgba(24,24,27,0.24)',
  // Between shadowCard and shadowFloating — the lift an interactive card (an
  // agent tile, a picker card) gets on hover/focus, one step more present
  // than resting state without jumping all the way to floating-panel depth.
  shadowHover: '0 4px 16px -8px rgba(24,24,27,0.16), 0 1px 3px rgba(24,24,27,0.06)',

  // Motion
  easeOut: [0.16, 1, 0.3, 1],
  durFast: 0.15,
  durBase: 0.28,
  durSlow: 0.45,
};

// The standard sr-only recipe — content stays in the accessibility tree and
// out of visual/layout flow. `width: '100%'; height: '100%'` (what a prior
// pass here used for the aria-live announcer) is NOT this: on a
// `position: absolute` element with no positioned ancestor, the containing
// block is the *viewport*, so `100%` resolves to the full viewport width
// regardless of where the element sits in the page, silently reopening
// horizontal scroll on any page that pads its content. `clip` alone doesn't
// remove an element from scrollWidth either — only its box's own dimensions
// do. Pin to 1px and keep `clip` (older engines) alongside `clip-path`
// (current). Use this — don't hand-roll another version of it.
export const visuallyHidden = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
  border: 0,
};

export default tutorTokens;
